import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { actionErrorMessage, isPermissionDenied } from "../../utils/network";
import { AccessDenied } from "../../components/RequirePermission";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";

import Sidebar from "../../components/Sidebar";
import { AppHeader } from "../../components/layout/AppHeader";
import { SpecializationTagInput } from "../../components/ui/SpecializationTagInput";
import { useBookAppointment } from "../../contexts/BookAppointmentContext";
import { useRole } from "../../hooks/useRole";
import { PatientAPI } from "../../services/patient.service";
import { TagsAPI } from "../../services/tags.service";
import { getPatientByDisplayId } from "../../services/patient.service";
import { DiagnosisAPI } from "../../services/diagnosis.service";
import type {
  VisitResponse, PrescriptionResponse, VitalsTrendPoint, MedicationResponse,
} from "../../services/diagnosis.service";
import { AppointmentAPI } from "../../services/appointment.service";
import type { AppointmentListItem, RescheduleHistoryItem } from "../../services/appointment.service";
import { getPatientInvoices, getInvoicePdf, sendInvoiceWhatsapp, sendInvoiceEmail } from "../../services/invoice.service";
import type { InvoiceResponse } from "../../schemas/invoice.schema";
import PaymentModal from "../billing/PaymentModal";
import { getInvoiceStatusPresentation } from "../../utils/billing";
import { DoctorAPI } from "../../services/doctor.service";
import type { DoctorListItem } from "../../services/doctor.service";
import { OnboardingAPI } from "../../services/onboarding.service";
import { apiRequest } from "../../services/api.client";
import { ROUTES } from "../../constants";
import type { PatientDbRecord } from "../../schemas/patient.schema";
import type { AllergyResponse } from "../../services/allergy.service";
import { getSessionRole } from "../../components/RequireAuth";
import DocumentsTab from "../../components/patients/documents/DocumentsTab";
import { NotesAPI, NotesApiError } from "../../services/visit-notes.service";
import type { PatientVisitNoteEntryResponse, VisitNoteEntryVersionResponse } from "../../services/visit-notes.service";
import { NoteForm, NoteRow, IcNote } from "../../components/VisitNotesPanel";
import DoctorNotesPanel from "../../components/DoctorNotesPanel";
import { isInsufficientCreditsError } from "../../services/credits.service";
import { useWalletBalance } from "../../hooks/useWalletBalance";
import { BuyCreditsModal } from "../../components/credits/BuyCreditsModal";
import { ToothChart, parseDentalFindingsText } from "../../components/ui/ToothChart";
import { CONDITION_BY_KEY } from "../../constants/dental.constants";
import type { ToothFinding } from "../../components/ui/ToothChart";
import EditPatientDrawer from "../../components/patients/EditPatientDrawer";
import ImmunizationTab from "../../components/ImmunizationTab";
import { GrowthChartPanel } from "../../components/growth/GrowthChartPanel";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";

// ─── Types ───────────────────────────────────────────────────────────────────

type TabId =
  | "overview" | "vitals" | "growth" | "appointments" | "medications" | "medical-history"
  | "treatment-plans" | "lab-results" | "immunizations" | "documents" | "notes" | "history" | "billing"
  | "visit-notes";

// `adminOrDoctorOnly` tabs are filtered out of the nav for other roles below —
// matches the backend's own require_roles(ADMIN, DOCTOR) gate on the notes API.
//
// Labeled "Visit Notes", NOT "Doctor Notes" — a separate, unrelated, unmerged
// feature (feature/doctor-notes-attributed-log, PR #204) already adds its own
// "Doctor Notes" tab to this same page (patient-level, single-author-chain
// notes). Same label would be a direct product collision; different label lets
// both survive independently. See PROGRESS_visit_progress_notes.md §11.
const TABS: { id: TabId; label: string; adminOrDoctorOnly?: boolean }[] = [
  { id: "overview",        label: "Overview" },
  { id: "vitals",          label: "Vitals" },
  { id: "growth",          label: "Growth Chart" },
  { id: "appointments",    label: "Appointments" },
  { id: "medications",     label: "Medications" },
  { id: "medical-history", label: "Medical History" },
  { id: "treatment-plans", label: "Treatment Plans" },
  { id: "lab-results",     label: "Lab Results" },
  { id: "immunizations",   label: "Immunizations" },
  { id: "documents",       label: "Documents" },
  { id: "notes",           label: "Doctor Notes" },
  { id: "history",         label: "History" },
  { id: "visit-notes",     label: "Visit Notes", adminOrDoctorOnly: true },
  { id: "billing",         label: "Billing" },
];

// Mirrors EditPatientDrawer's own parsing of these two JSON profile fields —
// same backend shapes, read-only rendering here instead of editable state.
function parseMedicalConditions(raw: unknown): string[] {
  if (!raw) return [];
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (!Array.isArray(parsed) || parsed.length === 0) return [];
    if (typeof parsed[0] === "object" && parsed[0] !== null) {
      return parsed.flatMap((obj: Record<string, string>) =>
        Object.entries(obj).filter(([, status]) => status === "Yes").map(([name]) => name)
      );
    }
    return parsed as string[];
  } catch {
    return [];
  }
}

function parseLifestyleOptions(raw: unknown): string[] {
  if (!raw) return [];
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (Array.isArray(parsed)) return parsed as string[];
    if (typeof parsed === "object" && parsed !== null) {
      const labels: string[] = [];
      if ((parsed as Record<string, string>).smoking === "Yes") labels.push("Smoker");
      if ((parsed as Record<string, string>).alcohol === "Yes") labels.push("Regular Drinker");
      return labels;
    }
    return [];
  } catch {
    return [];
  }
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function calcAge(dob?: string | null): string {
  if (!dob) return "—";
  const birth = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return String(age);
}

function fmtDate(iso?: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
  });
}

function fmtDateTime(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return (
    d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) +
    " • " +
    d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true })
  );
}

function initials(name?: string): string {
  if (!name) return "?";
  return name.split(" ").map((w) => w[0] ?? "").join("").slice(0, 2).toUpperCase();
}

const APPT_STATUS_COLORS: Record<string, string> = {
  completed:   "bg-green-100 text-green-700",
  finalized:   "bg-green-100 text-green-700",
  Finalized:   "bg-green-100 text-green-700",
  scheduled:   "bg-blue-100 text-blue-700",
  confirmed:   "bg-blue-100 text-blue-700",
  in_progress: "bg-amber-100 text-amber-700",
  cancelled:   "bg-red-100 text-red-700",
  no_show:     "bg-slate-100 text-slate-500",
};


const ALLERGY_SEVERITY_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  "Life-Threatening": { bg: "bg-red-50",    text: "text-red-700",    border: "border-red-300" },
  "Severe":           { bg: "bg-orange-50", text: "text-orange-700", border: "border-orange-300" },
  "Moderate":         { bg: "bg-amber-50",  text: "text-amber-700",  border: "border-amber-300" },
  "Mild":             { bg: "bg-yellow-50", text: "text-yellow-700", border: "border-yellow-200" },
};

// ─── Main Component ───────────────────────────────────────────────────────────

const PatientProfilePage: React.FC = () => {
  const { id: urlId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { openBookAppointmentModal } = useBookAppointment();
  // Header actions. Legacy fallback true — both were shown to every clinic role
  // pre-RBAC; /patients/:id/edit is additionally blocked by RequirePermission and
  // the PATCH/POST behind each button is enforced server-side.
  const { canOr, isAdmin, isDoctor } = useRole();
  const canEditPatient     = canOr("patients.update", true);
  const canBookAppointment = canOr("appointments.create", true);
  const canViewAppointments = canOr("appointments.view", true);
  // UI convenience gate only — the backend independently enforces visits.view
  // on every notes read regardless. Legacy fallback Admin/Doctor: the pre-RBAC
  // role gate on those endpoints.
  const canSeeVisitNotes = canOr("visits.view", isAdmin || isDoctor);
  // Start Visit opens the visit pad, which is Admin/Doctor by role AND
  // visits.write by grant (App.tsx) — a Receptionist used to see this button
  // and get bounced by the route guard after picking a doctor.
  const canStartVisit = (isAdmin || isDoctor) && canOr("visits.write", true);
  const canCreateBill = canOr("billing.write", true);
  const { isFeatureAccessible, getGateState } = useFeatureFlags();
  const immunizationEnabled = isFeatureAccessible("immunization");

  const tabParam = searchParams.get("tab") as TabId | null;
  const [activeTab, setActiveTab] = useState<TabId>(
    TABS.find((t) => t.id === tabParam) ? (tabParam as TabId) : "overview"
  );

  const [patient, setPatient] = useState<PatientDbRecord | null>(null);
  const [patientId, setPatientId] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [showEditDrawer, setShowEditDrawer] = useState(false);
  // Server refused the read (403). Holds the server's reason so the panel can
  // name the missing permission; null means "not refused".
  const [accessDenied, setAccessDenied] = useState<string | null>(null);
  const [showEmergencyModal, setShowEmergencyModal] = useState(false);

  const isGrowthChartEligible = useMemo(() => {
    if (!patient?.date_of_birth) return false;
    if (getGateState("pediatric_growth_charts") !== "active"
      && getGateState("pediatric_growth_charts") !== "trial") return false;
    const dob = new Date(patient.date_of_birth);
    if (isNaN(dob.getTime())) return false;
    const ageMs = Date.now() - dob.getTime();
    return ageMs / (365.25 * 24 * 60 * 60 * 1000) < 5;
  }, [patient?.date_of_birth, getGateState]);

  const visibleTabs = useMemo(
    () => TABS.filter((t) =>
      (!t.adminOrDoctorOnly || canSeeVisitNotes) &&
      (t.id !== "immunizations" || immunizationEnabled) &&
      (t.id !== "growth" || isGrowthChartEligible)
    ),
    [canSeeVisitNotes, immunizationEnabled, isGrowthChartEligible]
  );

  const [vitals, setVitals] = useState<VitalsTrendPoint[]>([]);
  const [appointments, setAppointments] = useState<AppointmentListItem[]>([]);
  const [appointmentsLoaded, setAppointmentsLoaded] = useState(false);
  const [rescheduleHistory, setRescheduleHistory] = useState<RescheduleHistoryItem[]>([]);
  const [rescheduleHistoryLoaded, setRescheduleHistoryLoaded] = useState(false);
  const [doctors, setDoctors] = useState<DoctorListItem[]>([]);
  // Doctor picker shown by handleStartVisit when the signed-in user isn't a
  // Doctor themselves — a dropdown anchored under the Start Visit button,
  // matching the doctor-select pattern already used on StartVisitPage.
  const [visitDoctorChoices, setVisitDoctorChoices] = useState<DoctorListItem[] | null>(null);
  const [startingVisit, setStartingVisit] = useState(false);
  const startVisitDropRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!visitDoctorChoices) return;
    const handler = (e: MouseEvent) => {
      if (startVisitDropRef.current && !startVisitDropRef.current.contains(e.target as Node)) {
        setVisitDoctorChoices(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [visitDoctorChoices]);

  const [visits, setVisits] = useState<VisitResponse[]>([]);
  const [visitPrescriptions, setVisitPrescriptions] = useState<Record<string, PrescriptionResponse[]>>({});
  const [visitsLoaded, setVisitsLoaded] = useState(false);

  const [invoices, setInvoices] = useState<InvoiceResponse[]>([]);
  const [invoicesLoaded, setInvoicesLoaded] = useState(false);

  const [tagSuggestions, setTagSuggestions] = useState<string[]>([]);
  useEffect(() => {
    TagsAPI.list()
      .then((tags) => setTagSuggestions(tags.map((t) => t.name)))
      .catch(() => {}); // non-fatal — autocomplete just stays empty
  }, []);

  const switchTab = (tab: TabId) => {
    setActiveTab(tab);
    setSearchParams({ tab }, { replace: true });
  };

  // ── Load patient + vitals eagerly ─────────────────────────────────────────
  useEffect(() => {
    if (!urlId) return;
    const isDisplayId = urlId.toUpperCase().startsWith("PAT-");

    const loadPatient = isDisplayId
      ? getPatientByDisplayId(urlId.toUpperCase())
      : PatientAPI.getById(urlId);

    loadPatient
      .then((p) => {
        setPatient(p);
        const pid = p.patient_id ?? urlId;
        setPatientId(pid);
        DiagnosisAPI.getVitalsTrend(pid, 12).then(setVitals).catch(() => {});
      })
      .catch(err => {
        // Refused, not missing — falling through to the "Patient not found"
        // screen would send the user hunting for a record that exists and that
        // they simply are not allowed to read.
        if (isPermissionDenied(err)) {
          // Always non-empty for a 403 — actionErrorMessage substitutes a
          // generic reason when the server sent none, so the panel below
          // cannot be skipped by a blank message.
          setAccessDenied(actionErrorMessage(err, ""));
          return;
        }
        toast.error(actionErrorMessage(err, "Failed to load patient"));
      })
      .finally(() => setLoading(false));
  }, [urlId]);

  // ── Load doctors when appointments tab opens ───────────────────────────────
  useEffect(() => {
    if (activeTab !== "appointments" || doctors.length > 0) return;
    DoctorAPI.getAll().then(setDoctors).catch(() => {});
  }, [activeTab, doctors.length]);

  // ── Load appointments (appointments + overview tabs) ──────────────────────
  useEffect(() => {
    const needs = activeTab === "appointments" || activeTab === "overview";
    if (!needs || !patientId || appointmentsLoaded) return;
    AppointmentAPI.list({ patient_id: patientId })
      .then((data) => { setAppointments(data); setAppointmentsLoaded(true); })
      .catch(() => {});
  }, [activeTab, patientId, appointmentsLoaded]);

  // ── Load reschedule history (appointments tab only) ────────────────────────
  useEffect(() => {
    if (activeTab !== "appointments" || !patientId || rescheduleHistoryLoaded) return;
    AppointmentAPI.getRescheduleHistory({ patient_id: patientId })
      .then((data) => { setRescheduleHistory(data); setRescheduleHistoryLoaded(true); })
      .catch(() => {});
  }, [activeTab, patientId, rescheduleHistoryLoaded]);

  // ── Load visits + rx (medications, treatment-plans, overview tabs) ─────────
  useEffect(() => {
    const needs =
      activeTab === "medications" ||
      activeTab === "treatment-plans" ||
      activeTab === "overview" ||
      activeTab === "history" ||
      activeTab === "visit-notes" ||
      activeTab === "documents";
    if (!needs || !patientId || visitsLoaded) return;
    apiRequest<{ visits: VisitResponse[]; prescriptions: Record<string, any[]> }>(
      ROUTES.PATIENT_VISITS_WITH_RX(patientId)
    )
      .then((res) => {
        setVisits(res.visits ?? []);
        const rxMap: Record<string, PrescriptionResponse[]> = {};
        for (const [vid, rxList] of Object.entries(res.prescriptions ?? {})) {
          rxMap[vid] = (rxList as any[]).map((rx) => ({ ...rx, visit_id: vid }));
        }
        setVisitPrescriptions(rxMap);
        setVisitsLoaded(true);
      })
      .catch(() => {});
  }, [activeTab, patientId, visitsLoaded]);

  // ── Load invoices ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (activeTab !== "billing" || !patientId || invoicesLoaded) return;
    getPatientInvoices(patientId)
      .then((data) => { setInvoices(data); setInvoicesLoaded(true); })
      .catch(() => { setInvoices([]); setInvoicesLoaded(true); });
  }, [activeTab, patientId, invoicesLoaded]);

  const doctorMap = useMemo(
    () => Object.fromEntries(doctors.map((d) => [d.doctor_id, d.name])),
    [doctors]
  );

  const allMedications = useMemo(() => {
    return visits.flatMap((v) =>
      (visitPrescriptions[v.visit_id] ?? []).flatMap((rx) =>
        rx.medications.map((m) => ({
          ...m, visit_date: v.visit_date, visit_id: v.visit_id,
        }))
      )
    );
  }, [visits, visitPrescriptions]);

  const recentAppts = useMemo(
    () =>
      [...appointments]
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .slice(0, 3),
    [appointments]
  );

  // ─── Render ───────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f5f5f7] flex">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center lg:pl-[var(--sidebar-w)] transition-[padding-left] duration-300 ease-in-out pt-14 lg:pt-0">
          <div className="h-8 w-8 rounded-full border-2 border-blue-200 border-t-blue-600 animate-spin" />
        </div>
      </div>
    );
  }

  // Grant revoked while this tab was open, or a deep link opened without it
  // (the route guard only runs on entry). Shown instead of "Patient not found",
  // which reads as a missing record rather than a refusal.
  if (accessDenied) {
    return (
      <div className="flex h-screen bg-[#f5f5f7] overflow-hidden">
        <Sidebar />
        <div className="flex-1 lg:pl-[var(--sidebar-w)] transition-[padding-left] duration-300 ease-in-out pt-14 lg:pt-0">
          <AccessDenied label="this patient" detail={accessDenied} />
        </div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="min-h-screen bg-[#f5f5f7] flex">
        <Sidebar />
        <div className="flex-1 flex flex-col items-center justify-center lg:pl-[var(--sidebar-w)] transition-[padding-left] duration-300 ease-in-out pt-14 lg:pt-0 gap-3">
          <p className="text-slate-500 text-sm">Patient not found.</p>
          <button onClick={() => navigate("/patients")} className="text-blue-600 text-sm underline">
            Back to Patients
          </button>
        </div>
      </div>
    );
  }

  const patientName = [patient.first_name, patient.last_name].filter(Boolean).join(" ");
  const emergencyContacts = patient.emergency_contacts ?? [];
  const primaryEC = emergencyContacts[0];
  const startVisitForDoctor = (doctorId: string) => {
    const displayId = patient?.display_id?.includes("?") ? null : patient?.display_id;
    navigate(`/90sdiagnosis?patient_id=${displayId ?? patientId}&doctor_id=${doctorId}`);
  };

  // Previously defaulted to the raw Cognito sub of whoever clicked the
  // button, which only ever resolved to a real doctor for the clinic's
  // self-registered founder — if that specific doctor got deactivated later,
  // starting a visit broke for the whole clinic with no way to pick anyone
  // else. A doctor starting their own visit is auto-resolved to themselves
  // (matches the lock used on StartVisitPage/BookAppointmentModal — a doctor
  // doesn't pick themselves). Anyone else (Admin/Receptionist) always sees
  // the doctor picker, even when there's currently only one active doctor —
  // "which doctor is this visit for" should always be an explicit choice for
  // non-doctor staff, not a silent auto-pick.
  const handleStartVisit = async () => {
    if (!patientId || startingVisit) return;
    setStartingVisit(true);
    try {
      if (isDoctor) {
        const me = await OnboardingAPI.getMe();
        if (me.doctor_id) {
          startVisitForDoctor(me.doctor_id);
          return;
        }
      }
      const activeDoctors = await DoctorAPI.list({ is_active: true });
      if (activeDoctors.length === 0) {
        toast.error("No active doctors available — activate a doctor before starting a visit.");
        return;
      }
      setVisitDoctorChoices(activeDoctors);
    } catch {
      toast.error("Session expired — please sign out and sign back in before starting a visit.");
    } finally {
      setStartingVisit(false);
    }
  };

  const handleTagsChange = async (tags: string[]) => {
    const previous = patient.tags ?? [];
    setPatient({ ...patient, tags }); // optimistic
    try {
      const updated = await PatientAPI.update(patientId, { patients: { tags } } as any);
      // Reconcile only the tags field (the backend normalizes casing/dedup)
      // instead of replacing the whole patient object — swapping in every
      // joined relation (allergies, emergency contacts, etc.) with fresh
      // references on every tag add forces the entire page to re-render for
      // no reason, which is what made this feel laggy.
      setPatient({ ...patient, tags: updated.tags });
    } catch {
      setPatient({ ...patient, tags: previous });
      toast.error("Failed to update tags. Please try again.");
    }
  };

  const latestVital = vitals[0];
  const lastVisitDate = vitals[0]?.visit_date ?? visits[0]?.visit_date ?? null;

  return (
    <div className="flex min-h-screen bg-[#f5f5f7]">
      <Sidebar />

      <div className="flex-1 min-w-0 lg:pl-[var(--sidebar-w)] transition-[padding-left] duration-300 ease-in-out pt-14 lg:pt-0">
        <AppHeader title="Patient Profile" />

        <div className="flex-1 p-4 sm:p-6 space-y-4">

          {/* Back link — top-left, matching the convention used elsewhere
              (e.g. KycReviewDetailPage) instead of tucked into the header's
              right-side actions slot. */}
          <button
            onClick={() => navigate("/patients")}
            className="text-[#0066cc] text-sm hover:underline flex items-center gap-1"
          >
            ‹ Back to Patients
          </button>

          {/* ─── Patient Card ─── */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
            <div className="flex flex-col lg:flex-row lg:items-start gap-6">

              {/* Left column: identity + contact details */}
              <div className="flex items-start gap-4 flex-1 min-w-0">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#0066ff] to-[#0040cc] flex items-center justify-center text-white text-xl font-bold flex-shrink-0 select-none">
                  {initials(patientName)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h1 className="text-[1.1rem] font-bold text-slate-900">{patientName}</h1>
                    {patient.display_id && (
                      <span className="px-2 py-0.5 bg-blue-50 text-blue-600 text-xs font-semibold rounded-full border border-blue-100">
                        {patient.display_id}
                      </span>
                    )}
                    {patient.excessive_reschedule_flag && (
                      <span className="px-2 py-0.5 bg-amber-50 text-amber-700 text-xs font-semibold rounded-full border border-amber-200">
                        Frequent reschedules
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-600 flex-wrap mb-3">
                    {calcAge(patient.date_of_birth) !== "—" && (
                      <span>{calcAge(patient.date_of_birth)} years</span>
                    )}
                    {patient.gender && (
                      <>
                        <span className="text-slate-300">•</span>
                        <span className="capitalize">{patient.gender}</span>
                      </>
                    )}
                    {patient.blood_group && (
                      <>
                        <span className="text-slate-300">•</span>
                        <span>Blood Group: {patient.blood_group}</span>
                      </>
                    )}
                  </div>

                  {/* Contact details — structured label/value grid instead of
                      an inline wrapped row, so it stays legible with 1-3 items. */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 max-w-lg">
                    {patient.contact?.contact_number && (
                      <span className="flex items-center gap-1.5 text-sm text-slate-600 min-w-0">
                        <PhoneIconSm />
                        <span className="truncate">+91{patient.contact.contact_number.replace(/^\+91/, "")}</span>
                      </span>
                    )}
                    {patient.contact?.email && (
                      <span className="flex items-center gap-1.5 text-sm text-slate-600 min-w-0">
                        <MailIconSm />
                        <span className="truncate">{patient.contact.email}</span>
                      </span>
                    )}
                    {patient.address && (
                      <span className="flex items-center gap-1.5 text-sm text-slate-600 min-w-0 sm:col-span-2">
                        <PinIconSm />
                        <span className="truncate">
                          {[
                            patient.address.address,
                            patient.address.city,
                            patient.address.state,
                            patient.address.pincode,
                          ].filter(Boolean).join(", ")}
                        </span>
                      </span>
                    )}
                  </div>

                  <div className="mt-3 max-w-sm">
                    <SpecializationTagInput
                      value={patient.tags ?? []}
                      onChange={handleTagsChange}
                      placeholder="Add a tag..."
                      suggestions={tagSuggestions}
                    />
                  </div>
                </div>
              </div>

              {/* Right column: actions only */}
              <div className="flex items-center gap-2 flex-shrink-0 self-start flex-wrap">
                {canStartVisit && (
                <div ref={startVisitDropRef} className="relative">
                  <button
                    onClick={handleStartVisit}
                    disabled={!patientId || startingVisit}
                    className="rounded-lg px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ background: 'linear-gradient(90deg, #10B981 0%, #22C55E 100%)' }}
                  >
                    {startingVisit ? "Starting…" : "Start Visit"}
                  </button>

                  {/* Doctor picker — anchored dropdown, same pattern as StartVisitPage's
                      doctor select. Shown only for non-Doctor staff (Admin/Receptionist);
                      a Doctor is auto-resolved to themselves before this ever renders. */}
                  {visitDoctorChoices && (
                    <div className="absolute left-0 top-full mt-1 w-64 bg-white border border-slate-200 rounded-xl shadow-lg z-50 overflow-hidden">
                      <p className="px-3 pt-2.5 pb-1.5 text-[11px] font-semibold text-slate-400 uppercase tracking-wide">
                        Start visit as
                      </p>
                      <ul className="max-h-56 overflow-y-auto pb-1">
                        {visitDoctorChoices.map((doc) => (
                          <li key={doc.doctor_id}>
                            <button
                              type="button"
                              onClick={() => { setVisitDoctorChoices(null); startVisitForDoctor(doc.doctor_id); }}
                              className="w-full text-left px-3 py-2 text-sm text-slate-800 hover:bg-slate-50"
                            >
                              {doc.name}{doc.specialization ? ` · ${doc.specialization}` : ""}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                )}
                {canBookAppointment && (
                  <button
                    onClick={() =>
                      // The deep link lands on /appointments, which needs View
                      // Appointments; a booking-only role gets the modal here.
                      canViewAppointments
                        ? navigate(`${ROUTES.APPOINTMENTS}?open_booking=true&patient_id=${patient?.display_id ?? patientId}`)
                        : openBookAppointmentModal()
                    }
                    disabled={!patientId}
                    className="rounded-lg px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ background: 'linear-gradient(90deg, #7C3AED 0%, #A78BFA 100%)' }}
                  >
                    Book Appointment
                  </button>
                )}
                {canEditPatient && (
                  <button
                    onClick={() => setShowEditDrawer(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 rounded-lg text-xs font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                  >
                    <EditIconSm />
                    Edit
                  </button>
                )}
              </div>
            </div>

            {/* Stat tiles — Emergency Contact tile is clickable: it used to
                be duplicated across a desktop panel, a mobile panel, and
                this tile (three renderings of the same data). Now this tile
                is the single entry point, opening a modal with the full list. */}
            <div
              className={`grid gap-2 sm:gap-3 mt-5 pt-5 border-t border-slate-100 ${
                patient.contact?.abha_id ? "grid-cols-2 lg:grid-cols-4" : "grid-cols-1 sm:grid-cols-3"
              }`}
            >
              <StatTile
                icon={<CalIconTile />}
                label="Registration Date"
                value={fmtDate(patient.created_at)}
              />
              <StatTile
                icon={<ActivityIconTile />}
                label="Last Visit"
                value={fmtDate(lastVisitDate)}
              />
              {patient.contact?.abha_id && (
                <StatTile
                  icon={<IdIconTile />}
                  label="ABHA Number"
                  value={patient.contact.abha_id}
                />
              )}
              <StatTile
                icon={<PhoneIconTile />}
                label="Emergency Contact"
                value={
                  emergencyContacts.length > 0
                    ? `${primaryEC?.name ?? ""}${emergencyContacts.length > 1 ? ` +${emergencyContacts.length - 1} more` : ""}`
                    : "—"
                }
                onClick={emergencyContacts.length > 0 ? () => setShowEmergencyModal(true) : undefined}
              />
              <StatTile
                icon={<RescheduleIconTile />}
                label="Reschedules"
                value={String(patient.reschedule_count ?? 0)}
              />
            </div>
          </div>

          {/* ─── Tab Bar ─── */}
          <div
            className="bg-white rounded-2xl border border-slate-100 shadow-sm px-2 py-1.5 sm:px-3 sm:py-2 flex items-center gap-1 overflow-x-auto"
            style={{ scrollbarWidth: "none", WebkitOverflowScrolling: "touch" } as React.CSSProperties}
          >
            {visibleTabs.map((t) => (
              <button
                key={t.id}
                onClick={() => switchTab(t.id)}
                className={`flex-shrink-0 text-xs sm:text-sm font-medium px-3 sm:px-4 py-2 rounded-xl transition-colors whitespace-nowrap ${
                  activeTab === t.id
                    ? "bg-[#0066ff] text-white shadow-sm"
                    : "text-slate-600 hover:bg-slate-50"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* ─── Tab Content ─── */}
          {activeTab === "overview" && (
            <OverviewTab
              patient={patient}
              latestVital={latestVital}
              allMedications={allMedications}
              recentAppts={recentAppts}
              onSeeAllAppointments={() => switchTab("appointments")}
              onSeeAllMedications={() => switchTab("medications")}
            />
          )}

          {activeTab === "vitals" && <VitalsTab vitals={vitals} />}

          {activeTab === "growth" && isGrowthChartEligible && (
            <GrowthChartPanel patientId={patientId} patientName={patient?.first_name ?? "the patient"} />
          )}

          {activeTab === "appointments" && (
            <AppointmentsTab
              appointments={appointments}
              doctorMap={doctorMap}
              rescheduleHistory={rescheduleHistory}
              onViewNotes={() => switchTab("treatment-plans")}
              onNewAppointment={() => openBookAppointmentModal()}
            />
          )}

          {activeTab === "medications" && (
            <MedicationsTab
              visits={visits}
              visitPrescriptions={visitPrescriptions}
            />
          )}

          {activeTab === "medical-history" && <MedicalHistoryTab patient={patient} />}

          {activeTab === "treatment-plans" && (
            <TreatmentPlansTab
              visits={visits}
              visitPrescriptions={visitPrescriptions}
            />
          )}

          {activeTab === "lab-results" && (
            <PlaceholderTab label="Lab Results" icon={
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="1.5">
                <path d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v11l-4 7h14l-4-7V3"/>
              </svg>
            } />
          )}
          {activeTab === "immunizations" && immunizationEnabled && (
            <ImmunizationTab patientId={patientId!} editable />
          )}

          {activeTab === "documents" && patientId && (
            <div className="space-y-6">
              <DocumentsTab
                patientId={patientId}
                userRole={getSessionRole() ?? undefined}
              />
              {visits.some(v => v.chief_complaint?.includes("Dental Findings:")) && (
                <DentalDocumentsTab visits={visits} />
              )}
            </div>
          )}

          {activeTab === "notes" && <DoctorNotesPanel patientId={patientId} />}

          {activeTab === "history" && (
            <HistoryTab visits={visits} visitPrescriptions={visitPrescriptions} patientId={patientId} />
          )}

          {activeTab === "visit-notes" && canSeeVisitNotes && (
            <PatientVisitNotesTab patientId={patientId} />
          )}

          {activeTab === "billing" && (
            <BillingTab
              invoices={invoices}
              highlightInvoiceId={searchParams.get("invoice_id")}
              onCreateBill={canCreateBill ? () => navigate(`/billing?patientId=${patient?.display_id || patientId}`) : undefined}
              onInvoiceUpdate={(updated) => setInvoices((prev) => prev.map((inv) => inv.invoice_id === updated.invoice_id ? updated : inv))}
            />
          )}
        </div>
      </div>

      <EditPatientDrawer
        patientId={patientId}
        patient={patient}
        isOpen={showEditDrawer}
        onClose={() => setShowEditDrawer(false)}
        onSaved={(updated) => setPatient(updated)}
      />

      {/* Emergency Contacts modal — single place these are viewed from (was
          previously duplicated across a desktop panel, a mobile panel, and
          this same stat tile). Editing still happens in the Edit drawer's
          Family tab; "Manage contacts" below jumps straight there. */}
      {showEmergencyModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setShowEmergencyModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-[480px] max-w-full max-h-[80vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h3 className="text-base font-semibold text-slate-900">
                Emergency Contacts ({emergencyContacts.length})
              </h3>
              <button
                onClick={() => setShowEmergencyModal(false)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                aria-label="Close"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="overflow-y-auto p-6 space-y-3">
              {emergencyContacts.map((ec, i) => (
                <div key={i} className="rounded-xl border border-slate-100 bg-slate-50/60 p-4">
                  <div className="flex items-center gap-2 flex-wrap mb-1.5">
                    <span className="font-semibold text-slate-800">{ec.name}</span>
                    {ec.relationship && (
                      <span className="px-2 py-0.5 bg-white text-slate-500 text-xs rounded-full border border-slate-200 capitalize">
                        {ec.relationship}
                      </span>
                    )}
                  </div>
                  <div className="space-y-1">
                    {ec.contact_number && (
                      <p className="flex items-center gap-1.5 text-sm text-slate-600">
                        <PhoneIconSm /> {ec.contact_number}
                      </p>
                    )}
                    {ec.email && (
                      <p className="flex items-center gap-1.5 text-sm text-slate-600">
                        <MailIconSm /> {ec.email}
                      </p>
                    )}
                    {(ec.address || ec.city || ec.state || ec.pincode) && (
                      <p className="flex items-center gap-1.5 text-sm text-slate-600">
                        <PinIconSm />
                        {[ec.address, ec.city, ec.state, ec.pincode].filter(Boolean).join(", ")}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="px-6 py-4 border-t border-slate-100">
              <button
                onClick={() => { setShowEmergencyModal(false); setShowEditDrawer(true); }}
                className="text-sm font-medium text-blue-600 hover:text-blue-700"
              >
                Manage contacts →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── StatTile ─────────────────────────────────────────────────────────────────

const StatTile: React.FC<{ icon: React.ReactNode; label: string; value: string; onClick?: () => void }> = ({
  icon, label, value, onClick,
}) => {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      onClick={onClick}
      type={onClick ? "button" : undefined}
      className={`rounded-xl border border-slate-100 bg-slate-50/60 px-4 py-3 text-left w-full ${
        onClick ? "hover:border-blue-200 hover:bg-blue-50/40 transition-colors cursor-pointer" : ""
      }`}
    >
      <div className="flex items-center gap-1.5 text-slate-400 mb-1">
        {icon}
        <span className="text-xs font-medium text-slate-500">{label}</span>
      </div>
      <p className={`text-sm font-semibold truncate ${onClick ? "text-blue-600" : "text-slate-800"}`} title={value}>{value}</p>
    </Tag>
  );
};

// ─── OverviewTab ──────────────────────────────────────────────────────────────

const OverviewTab: React.FC<{
  patient: PatientDbRecord;
  latestVital?: VitalsTrendPoint;
  allMedications: (MedicationResponse & { visit_date: string })[];
  recentAppts: AppointmentListItem[];
  onSeeAllAppointments: () => void;
  onSeeAllMedications: () => void;
}> = ({ patient, latestVital, allMedications, recentAppts, onSeeAllAppointments, onSeeAllMedications }) => {
  const allergies = ((patient as any).allergies ?? []) as AllergyResponse[];
  const activeMeds = allMedications.slice(0, 9);

  return (
    <div className="space-y-4">
      {/* AI Health Summary (placeholder) */}
      <div className="rounded-2xl p-5" style={{ background: "linear-gradient(135deg, #6d28d9 0%, #7c3aed 40%, #a21caf 100%)" }}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8">
                <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z"/>
              </svg>
            </div>
            <div>
              <p className="text-sm font-semibold text-white">AI Health Summary</p>
              <p className="text-xs text-white/60">AI-powered insights coming soon</p>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-center py-2 gap-2 text-sm text-white/70">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          Coming soon
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Current Vitals */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-slate-800">Current Vitals</h3>
            <ActivityIconTile />
          </div>
          {latestVital ? (
            <div className="space-y-2">
              {(
                [
                  {
                    icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>,
                    bg: "bg-red-50",
                    label: "Blood Pressure",
                    value: latestVital.blood_pressure_systolic && latestVital.blood_pressure_diastolic
                      ? `${latestVital.blood_pressure_systolic}/${latestVital.blood_pressure_diastolic}`
                      : "—",
                  },
                  {
                    icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#ec4899" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>,
                    bg: "bg-pink-50",
                    label: "Heart Rate",
                    value: latestVital.heart_rate ? `${latestVital.heart_rate} bpm` : "—",
                  },
                  {
                    icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#f97316" strokeWidth="2"><path d="M14 14.76V3.5a2.5 2.5 0 00-5 0v11.26a4.5 4.5 0 105 0z"/></svg>,
                    bg: "bg-orange-50",
                    label: "Temperature",
                    value: latestVital.temperature ? `${latestVital.temperature}°F` : "—",
                  },
                  {
                    icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2"><path d="M12 2.69l5.66 5.66a8 8 0 11-11.31 0z"/></svg>,
                    bg: "bg-blue-50",
                    label: "SpO2",
                    value: latestVital.oxygen_saturation ? `${latestVital.oxygen_saturation}%` : "—",
                  },
                  {
                    icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#a855f7" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>,
                    bg: "bg-purple-50",
                    label: "Weight",
                    value: latestVital.weight
                      ? `${latestVital.weight} ${latestVital.weight_unit ?? "kg"}`
                      : "—",
                  },
                ] as { icon: React.ReactNode; bg: string; label: string; value: string }[]
              ).map((v) => (
                <div key={v.label} className={`flex items-center justify-between rounded-lg px-3 py-2 ${v.bg}`}>
                  <div className="flex items-center gap-2">
                    {v.icon}
                    <span className="text-sm text-slate-600">{v.label}</span>
                  </div>
                  <span className="text-sm font-semibold text-slate-800">{v.value}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400 text-center py-6">No vitals recorded yet.</p>
          )}
        </div>

        {/* Active Medications */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-slate-800">Active Medications</h3>
            <button onClick={onSeeAllMedications} className="text-[#0066ff] hover:text-[#0040cc]">
              <PencilIconSm />
            </button>
          </div>
          {activeMeds.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2">
              {activeMeds.map((m, i) => (
                <div key={i} className="rounded-xl border border-slate-100 bg-slate-50 p-3">
                  <div className="flex items-start justify-between gap-1 mb-1">
                    <p className="text-sm font-semibold text-slate-800 leading-tight">{m.name}</p>
                    <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-[10px] font-medium rounded flex-shrink-0">
                      Active
                    </span>
                  </div>
                  <p className="text-xs text-slate-500">{m.dosage} • {m.frequency}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400 text-center py-6">No medications on record.</p>
          )}
        </div>
      </div>

      {/* Allergies & Warnings */}
      {allergies.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-slate-800">Allergies &amp; Warnings</h3>
            <div className="w-6 h-6 rounded-full bg-red-50 flex items-center justify-center">
              <span className="text-xs text-red-500">!</span>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {allergies.map((a, i) => {
              const colors = ALLERGY_SEVERITY_COLORS[a.severity] ?? ALLERGY_SEVERITY_COLORS["Mild"];
              return (
                <div
                  key={i}
                  className={`rounded-xl border-l-4 p-3 ${colors.bg} ${colors.border}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-semibold text-slate-800">{a.allergen}</p>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${colors.text} ${colors.bg}`}>
                      {a.severity}
                    </span>
                  </div>
                  {a.reaction && (
                    <p className="text-xs text-slate-500 mt-0.5">Reaction: {a.reaction}</p>
                  )}
                  {a.verified_date && (
                    <p className="text-xs text-slate-400 mt-0.5">
                      Recorded: {fmtDate(a.verified_date)}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent Appointments */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-slate-800">Recent Appointments</h3>
          <button
            onClick={onSeeAllAppointments}
            className="text-[#0066ff] hover:text-[#0040cc]"
          >
            <CalIconTile />
          </button>
        </div>
        {recentAppts.length > 0 ? (
          <div className="space-y-2">
            {recentAppts.map((a) => (
              <div key={a.appointment_id} className="flex items-center gap-3 py-2.5 border-b border-slate-50 last:border-0">
                <div className="w-9 h-9 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <CalIconSm />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 capitalize">
                    {a.notes ?? "Appointment"}
                  </p>
                  <p className="text-xs text-slate-400">{fmtDate(a.created_at)}</p>
                </div>
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium capitalize flex-shrink-0 ${APPT_STATUS_COLORS[a.status] ?? "bg-slate-100 text-slate-600"}`}>
                  {a.status.replace("_", " ")}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-400 text-center py-6">No appointments yet.</p>
        )}
      </div>
    </div>
  );
};

// ─── VitalsTab ─────────────────────────────────────────────────────────────────

const VitalsTab: React.FC<{ vitals: VitalsTrendPoint[] }> = ({ vitals }) => {
  // Plot every reading as its own point so multiple visits on the same day all appear.
  const chartData = [...vitals]
    .sort((a, b) => new Date(a.visit_date).getTime() - new Date(b.visit_date).getTime())
    .map((v) => ({
      date: new Date(v.visit_date).toLocaleDateString("en-IN", { month: "short", day: "2-digit" }),
      systolic:  v.blood_pressure_systolic  ?? undefined,
      diastolic: v.blood_pressure_diastolic ?? undefined,
      weight:    v.weight                  ?? undefined,
      heartRate: v.heart_rate              ?? undefined,
    }));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* BP Trend */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
          <h3 className="text-sm font-semibold text-slate-800 mb-4">Blood Pressure Trend</h3>
          {chartData.some((d) => d.systolic || d.diastolic) ? (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 8, right: 16, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="sysGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#0ea5e9" stopOpacity={0.18} />
                      <stop offset="100%" stopColor="#0ea5e9" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="diaGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#14b8a6" stopOpacity={0.18} />
                      <stop offset="100%" stopColor="#14b8a6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} domain={["auto", "auto"]} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0", boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }} />
                  <Area type="natural" dataKey="systolic" name="Systolic" stroke="#0ea5e9" fill="url(#sysGrad)" strokeWidth={2} dot={{ r: 4, fill: "#fff", stroke: "#0ea5e9", strokeWidth: 2 }} activeDot={{ r: 5 }} connectNulls />
                  <Area type="natural" dataKey="diastolic" name="Diastolic" stroke="#14b8a6" fill="url(#diaGrad)" strokeWidth={2} dot={{ r: 4, fill: "#fff", stroke: "#14b8a6", strokeWidth: 2 }} activeDot={{ r: 5 }} connectNulls />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-sm text-slate-400 text-center py-12">No blood pressure data available.</p>
          )}
        </div>

        {/* Weight Trend */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
          <h3 className="text-sm font-semibold text-slate-800 mb-4">Weight Trend</h3>
          {chartData.some((d) => d.weight) ? (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 8, right: 16, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="weightGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} domain={["auto", "auto"]} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0", boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }} formatter={(v) => [`${v} kg`, "Weight"]} />
                  <Area type="natural" dataKey="weight" name="Weight" stroke="#8b5cf6" fill="url(#weightGrad)" strokeWidth={2} dot={{ r: 4, fill: "#fff", stroke: "#8b5cf6", strokeWidth: 2 }} activeDot={{ r: 5 }} connectNulls />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-sm text-slate-400 text-center py-12">No weight data available.</p>
          )}
        </div>
      </div>

      {/* Vitals History Table */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
        <h3 className="text-sm font-semibold text-slate-800 mb-4">Vitals History</h3>
        {vitals.length > 0 ? (
          <div className="mt-4 overflow-x-auto -mx-5 px-5">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-semibold text-slate-400 border-b border-slate-100">
                  {["Date", "Blood Pressure", "Heart Rate", "Temperature", "SpO2", "Weight"].map((h) => (
                    <th key={h} className="pb-2 pr-4 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {vitals.map((v) => (
                  <tr key={v.vital_id} className="border-b border-slate-50 last:border-0">
                    <td className="py-2.5 pr-4 whitespace-nowrap text-slate-600">{fmtDate(v.visit_date)}</td>
                    <td className="py-2.5 pr-4 whitespace-nowrap font-medium">
                      {v.blood_pressure_systolic && v.blood_pressure_diastolic
                        ? `${v.blood_pressure_systolic}/${v.blood_pressure_diastolic}`
                        : "—"}
                    </td>
                    <td className="py-2.5 pr-4 whitespace-nowrap">{v.heart_rate ? `${v.heart_rate} bpm` : "—"}</td>
                    <td className="py-2.5 pr-4 whitespace-nowrap">{v.temperature ? `${v.temperature}°F` : "—"}</td>
                    <td className="py-2.5 pr-4 whitespace-nowrap">{v.oxygen_saturation ? `${v.oxygen_saturation}%` : "—"}</td>
                    <td className="py-2.5 pr-4 whitespace-nowrap">
                      {v.weight ? `${v.weight} ${v.weight_unit ?? "kg"}` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-slate-400 text-center py-6">No vitals history.</p>
        )}
      </div>
    </div>
  );
};

// ─── AppointmentsTab ──────────────────────────────────────────────────────────

const AppointmentsTab: React.FC<{
  appointments: AppointmentListItem[];
  doctorMap: Record<string, string>;
  rescheduleHistory: RescheduleHistoryItem[];
  onViewNotes: () => void;
  onNewAppointment: () => void;
}> = ({ appointments, doctorMap, rescheduleHistory, onViewNotes, onNewAppointment }) => {
  const navigate = useNavigate();
  // Legacy fallback true — the button was shown to every clinic role pre-RBAC.
  // POST /appointments enforces appointments.create regardless.
  const { canOr } = useRole();
  const canBookAppointment = canOr("appointments.create", true);
  const [filter, setFilter] = useState<string>("all");
  const [search, setSearch] = useState("");

  const filtered = appointments.filter((a) => {
    if (filter !== "all" && a.status !== filter) return false;
    if (search && !a.notes?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const sorted = [...filtered].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <h3 className="text-sm font-semibold text-slate-800">Appointment History</h3>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="all">All</option>
            <option value="scheduled">Scheduled</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="no_show">No Show</option>
          </select>
          <input
            type="text"
            placeholder="Search by notes..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-500 w-36"
          />
          {canBookAppointment && (
            <button
              onClick={onNewAppointment}
              className="flex items-center gap-1 text-xs font-medium px-3 py-1.5 bg-[#0066ff] text-white rounded-lg hover:bg-[#0051cc] transition-colors"
            >
              + New Appointment
            </button>
          )}
        </div>
      </div>

      {sorted.length > 0 ? (
        <div className="space-y-2">
          {sorted.map((a) => (
            <div
              key={a.appointment_id}
              onClick={() => navigate(`/appointments/confirmation/${a.appointment_id}`)}
              className="flex items-center gap-3 py-3 border-b border-slate-50 last:border-0 cursor-pointer hover:bg-slate-50 rounded-xl px-2 -mx-2 transition-colors"
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                a.status === "completed" ? "bg-green-50" : "bg-blue-50"
              }`}>
                <CalIconSm />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800">
                  {a.notes ?? "Appointment"}
                </p>
                <p className="text-xs text-slate-400">
                  {fmtDateTime(a.created_at)}
                  {doctorMap[a.doctor_id] && (
                    <> &bull; <span className="font-medium">{doctorMap[a.doctor_id]}</span></>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {a.status === "cancelled" && a.cancellation_reason === "rescheduled" ? (
                  <span
                    onClick={(e) => {
                      if (!a.rescheduled_to_id) return;
                      e.stopPropagation();
                      navigate(`/appointments/confirmation/${a.rescheduled_to_id}`);
                    }}
                    className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 hover:underline cursor-pointer"
                    title={a.rescheduled_to_id ? "View the new appointment" : undefined}
                  >
                    Rescheduled
                  </span>
                ) : (
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${APPT_STATUS_COLORS[a.status] ?? "bg-slate-100 text-slate-600"}`}>
                    {a.status.replace("_", " ")}
                  </span>
                )}
                {a.visit_id && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onViewNotes(); }}
                    className="hidden sm:flex text-xs text-[#0066ff] font-medium hover:underline items-center gap-0.5"
                  >
                    View Notes
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M9 18l6-6-6-6" /></svg>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-slate-400 text-center py-10">No appointments found.</p>
      )}

      {rescheduleHistory.length > 0 && (
        <div className="mt-6 pt-5 border-t border-slate-100">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">
            Reschedule History
          </p>
          <div className="space-y-2">
            {rescheduleHistory.map((h) => (
              <div key={h.history_id} className="flex items-center gap-3 py-2 text-sm">
                <span className="text-slate-500 flex-shrink-0">{fmtDateTime(h.old_time)}</span>
                <svg className="w-3 h-3 text-slate-300 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M5 12h14M13 5l7 7-7 7" /></svg>
                <span className="text-slate-700 font-medium flex-shrink-0">{fmtDateTime(h.new_time)}</span>
                <span className="text-slate-300">•</span>
                <span className="text-slate-500 capitalize">{h.initiator}-initiated</span>
                <span className="text-slate-300">•</span>
                <span className="text-slate-500 capitalize">{h.reason.replace("_", " ")}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── MedicalHistoryTab ─────────────────────────────────────────────────────────

const MedicalHistoryTab: React.FC<{ patient: PatientDbRecord | null }> = ({ patient }) => {
  const profile = patient?.medical_profile;
  const conditions = parseMedicalConditions(profile?.medical_conditions);
  const lifestyle = parseLifestyleOptions(profile?.lifestyle_options);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
          <h3 className="text-sm font-semibold text-slate-800 mb-3">Medical Conditions</h3>
          {conditions.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {conditions.map((c) => (
                <span key={c} className="inline-flex items-center rounded-full bg-red-50 text-red-700 border border-red-100 px-3 py-1 text-xs font-medium">
                  {c}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400">None recorded.</p>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
          <h3 className="text-sm font-semibold text-slate-800 mb-3">Lifestyle</h3>
          {lifestyle.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {lifestyle.map((l) => (
                <span key={l} className="inline-flex items-center rounded-full bg-amber-50 text-amber-700 border border-amber-100 px-3 py-1 text-xs font-medium">
                  {l}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400">None recorded.</p>
          )}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
        <h3 className="text-sm font-semibold text-slate-800 mb-3">Current Medications</h3>
        <p className="text-sm text-slate-600 whitespace-pre-wrap">
          {profile?.current_medications?.trim() || "Not recorded."}
        </p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
        <h3 className="text-sm font-semibold text-slate-800 mb-3">Previous Surgeries</h3>
        <p className="text-sm text-slate-600 whitespace-pre-wrap">
          {profile?.previous_surgeries?.trim() || "Not recorded."}
        </p>
      </div>
    </div>
  );
};

// ─── MedicationsTab ────────────────────────────────────────────────────────────

const MedicationsTab: React.FC<{
  visits: VisitResponse[];
  visitPrescriptions: Record<string, PrescriptionResponse[]>;
}> = ({ visits, visitPrescriptions }) => {
  const allMeds = useMemo(() => {
    return visits.flatMap((v) =>
      (visitPrescriptions[v.visit_id] ?? []).flatMap((rx) =>
        rx.medications.map((m) => ({
          ...m,
          visit_date: v.visit_date,
          diagnosis_label: rx.diagnosis_label,
        }))
      )
    );
  }, [visits, visitPrescriptions]);

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
      <h3 className="text-sm font-semibold text-slate-800 mb-4">
        All Medications
        {allMeds.length > 0 && (
          <span className="ml-2 text-xs text-slate-400 font-normal">({allMeds.length} total)</span>
        )}
      </h3>

      {allMeds.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
          {allMeds.map((m, i) => (
            <div key={i} className="rounded-xl border border-slate-100 bg-slate-50 p-4">
              <div className="flex items-start justify-between gap-2 mb-2">
                <p className="text-sm font-semibold text-slate-800">{m.name}</p>
                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-[10px] font-medium rounded-full flex-shrink-0">
                  Active
                </span>
              </div>
              <p className="text-xs text-slate-600">{m.dosage} • {m.frequency}</p>
              {m.duration && <p className="text-xs text-slate-400 mt-0.5">Duration: {m.duration}</p>}
              {m.instructions && <p className="text-xs text-slate-400 mt-0.5">{m.instructions}</p>}
              <p className="text-xs text-slate-300 mt-2">{fmtDate(m.visit_date)}</p>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-slate-400 text-center py-10">No medications on record.</p>
      )}
    </div>
  );
};

// ─── TreatmentPlansTab (completed visits only) ────────────────────────────────

const TreatmentPlansTab: React.FC<{
  visits: VisitResponse[];
  visitPrescriptions: Record<string, PrescriptionResponse[]>;
}> = ({ visits, visitPrescriptions }) => {
  const completed = [...visits]
    .filter((v) => ["completed", "finalized", "Finalized"].includes(v.status))
    .sort((a, b) => new Date(b.visit_date).getTime() - new Date(a.visit_date).getTime());

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-slate-800">Visit History</h3>
        <span className="text-xs text-slate-400">{completed.length} visit{completed.length !== 1 ? "s" : ""}</span>
      </div>
      {completed.length > 0 ? (
        <div className="space-y-4">
          {completed.map((v) => (
            <VisitCard key={v.visit_id} visit={v} prescriptions={visitPrescriptions[v.visit_id] ?? []} />
          ))}
        </div>
      ) : (
        <p className="text-sm text-slate-400 text-center py-10">No completed visits on record.</p>
      )}
    </div>
  );
};

const VisitCard: React.FC<{
  visit: VisitResponse;
  prescriptions: PrescriptionResponse[];
}> = ({ visit, prescriptions }) => {
  const [pdfLoading, setPdfLoading] = useState(false);
  const allMeds = prescriptions.flatMap((rx) => rx.medications);
  const advice = prescriptions[0]?.advice ?? visit.advice;
  const firstRx = prescriptions[0];

  const handleDownloadPdf = async () => {
    if (!firstRx) return;
    setPdfLoading(true);
    try {
      // Always regenerate to ensure latest PDF template is used
      const result = await DiagnosisAPI.generatePdf(visit.visit_id, firstRx.prescription_id);
      if (result.pdf_url) window.open(result.pdf_url, "_blank");
    } catch {
      toast.error("Could not download prescription PDF");
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-100 p-4">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div>
          <p className="text-sm font-semibold text-slate-800">{fmtDate(visit.visit_date)}</p>
          {visit.chief_complaint && (
            <p className="text-xs text-slate-500 mt-0.5">Chief complaint: {visit.chief_complaint}</p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {firstRx && (
            <button
              onClick={handleDownloadPdf}
              disabled={pdfLoading}
              className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 transition-colors"
            >
              {pdfLoading ? (
                <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
              ) : (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              )}
              Rx PDF
            </button>
          )}
          <span className="px-2 py-0.5 rounded-full text-xs font-medium capitalize bg-green-100 text-green-700">
            completed
          </span>
        </div>
      </div>

      {visit.notes && (
        <div className="mb-3">
          <p className="text-xs font-semibold text-slate-400 mb-1">Notes</p>
          <p className="text-sm text-slate-600">{visit.notes}</p>
        </div>
      )}

      {allMeds.length > 0 && (
        <div className="mb-3">
          <p className="text-xs font-semibold text-slate-400 mb-1.5">Medications</p>
          <div className="flex flex-wrap gap-1.5">
            {allMeds.map((m, i) => (
              <span key={i} className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full">
                {m.name} {m.dosage} · {m.frequency}
              </span>
            ))}
          </div>
        </div>
      )}

      {advice && (
        <div className="mb-2">
          <p className="text-xs font-semibold text-slate-400 mb-1">Advice</p>
          <p className="text-sm text-slate-600">{advice}</p>
        </div>
      )}

      {visit.follow_up_date && (
        <p className="text-xs text-slate-400 mt-2">
          Follow-up: <span className="font-medium text-slate-600">{fmtDate(visit.follow_up_date)}</span>
        </p>
      )}
    </div>
  );
};

// ─── HistoryTab ───────────────────────────────────────────────────────────────

const VISIT_STATUS_STYLE: Record<string, { dot: string; text: string; label: string }> = {
  Draft:     { dot: "bg-slate-400", text: "text-slate-600", label: "Draft" },
  draft:     { dot: "bg-slate-400", text: "text-slate-600", label: "Draft" },
  Finalized: { dot: "bg-emerald-500", text: "text-emerald-700", label: "Finalized" },
  finalized: { dot: "bg-emerald-500", text: "text-emerald-700", label: "Finalized" },
  completed: { dot: "bg-emerald-500", text: "text-emerald-700", label: "Completed" },
  Cancelled: { dot: "bg-red-500", text: "text-red-600", label: "Cancelled" },
  cancelled: { dot: "bg-red-500", text: "text-red-600", label: "Cancelled" },
};

const HistoryTab: React.FC<{
  visits: VisitResponse[];
  visitPrescriptions: Record<string, PrescriptionResponse[]>;
  patientId: string;
}> = ({ visits, visitPrescriptions, patientId }) => {
  const navigate = useNavigate();
  const { canOr, isAdmin, isDoctor } = useRole();
  // "Edit" reopens the visit pad on a draft — same gate as Start Visit.
  const canEditDraft = (isAdmin || isDoctor) && canOr("visits.write", true);
  const [soapVisit, setSoapVisit] = useState<VisitResponse | null>(null);
  const sorted = [...visits].sort((a, b) => new Date(b.visit_date).getTime() - new Date(a.visit_date).getTime());

  return (
    <>
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-900">Visit History</h3>
          <span className="text-xs text-slate-400">{sorted.length} visit{sorted.length !== 1 ? "s" : ""}</span>
        </div>

        {sorted.length === 0 ? (
          <div className="text-center py-16 text-sm text-slate-400">No visits found.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {sorted.map((v) => {
              const st = VISIT_STATUS_STYLE[v.status] ?? VISIT_STATUS_STYLE.Draft;
              const isDraft = v.status === "Draft" || v.status === "draft";
              const isFinalized = ["Finalized", "finalized", "completed"].includes(v.status);
              const rxList = visitPrescriptions[v.visit_id] ?? [];
              const firstRx = rxList[0];

              return (
                <div key={v.visit_id} className="px-5 py-4 hover:bg-slate-50/50 transition">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      {/* Visit icon */}
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isFinalized ? "bg-emerald-50" : "bg-slate-100"}`}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={isFinalized ? "#059669" : "#94a3b8"} strokeWidth="2">
                          <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/>
                        </svg>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-800">{fmtDate(v.visit_date)}</p>
                        {v.chief_complaint && (
                          <p className="text-xs text-slate-500 mt-0.5 truncate">{v.chief_complaint}</p>
                        )}
                        {v.assessment && (
                          <p className="text-xs text-slate-400 mt-0.5 truncate">Dx: {v.assessment}</p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      {/* Status badge */}
                      <span className="inline-flex items-center gap-1.5">
                        <span className={`w-2 h-2 rounded-full ${st.dot}`} />
                        <span className={`text-xs font-medium ${st.text}`}>{st.label}</span>
                      </span>

                      {/* View SOAP note */}
                      {isFinalized && (
                        <button
                          onClick={() => setSoapVisit(v)}
                          title="View SOAP note"
                          className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition"
                        >
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                        </button>
                      )}

                      {/* Download PDF */}
                      {isFinalized && firstRx && (
                        <button
                          onClick={async () => {
                            try {
                              // Always regenerate to ensure latest PDF template is used
                              const result = await DiagnosisAPI.generatePdf(v.visit_id, firstRx.prescription_id);
                              if (result.pdf_url) window.open(result.pdf_url, "_blank");
                            } catch { toast.error("PDF not available."); }
                          }}
                          title="Download PDF"
                          className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition"
                        >
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                        </button>
                      )}

                      {/* Edit draft */}
                      {isDraft && canEditDraft && (
                        <button
                          onClick={() => navigate(`/continue-diagnosis?visit_id=${v.visit_id}&patient_id=${patientId}&doctor_id=${v.doctor_id}`)}
                          className="px-2.5 py-1 text-xs font-medium text-slate-600 bg-slate-100 border border-slate-200 rounded-lg hover:bg-slate-200 transition"
                        >
                          Edit
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* SOAP Note Viewer Modal */}
      {soapVisit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setSoapVisit(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-[700px] max-w-[95vw] max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h3 className="text-base font-semibold text-slate-900">Visit - {fmtDate(soapVisit.visit_date)}</h3>
                {soapVisit.chief_complaint && <p className="text-xs text-slate-500 mt-0.5">{soapVisit.chief_complaint}</p>}
              </div>
              <button onClick={() => setSoapVisit(null)} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="overflow-y-auto p-6 space-y-4">
              {soapVisit.chief_complaint && <SoapField label="Chief Complaint" value={soapVisit.chief_complaint} />}
              {(soapVisit as any).subjective && <SoapField label="Subjective" value={(soapVisit as any).subjective} />}
              {(soapVisit as any).objective && <SoapField label="Objective" value={(soapVisit as any).objective} />}
              {soapVisit.assessment && <SoapField label="Assessment" value={soapVisit.assessment} />}
              {soapVisit.notes && <SoapField label="Plan / Notes" value={soapVisit.notes} />}
              {soapVisit.advice && <SoapField label="Advice" value={soapVisit.advice} />}
              {soapVisit.follow_up_date && <SoapField label="Follow-up" value={fmtDate(soapVisit.follow_up_date)} />}
              {(visitPrescriptions[soapVisit.visit_id] ?? []).flatMap(rx => rx.medications).length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Medications</p>
                  <div className="flex flex-wrap gap-1.5">
                    {(visitPrescriptions[soapVisit.visit_id] ?? []).flatMap(rx => rx.medications).map((m, i) => (
                      <span key={i} className="px-2.5 py-1 bg-blue-50 text-blue-700 text-xs rounded-full">{m.name} {m.dosage} - {m.frequency}</span>
                    ))}
                  </div>
                </div>
              )}
              {!soapVisit.chief_complaint && !(soapVisit as any).subjective && !(soapVisit as any).objective && !soapVisit.assessment && !soapVisit.notes && (
                <p className="text-sm text-slate-400 text-center py-6">No SOAP note data available for this visit.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const SoapField: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">{label}</p>
    <p className="text-sm text-slate-700 whitespace-pre-wrap">{value}</p>
  </div>
);

// ─── PatientVisitNotesTab ──────────────────────────────────────────────────────
// Aggregates the attributed, versioned Visit Notes entries across every one of
// this patient's visits (the notes API itself is visit-scoped, not patient-scoped
// — see PROGRESS_visit_progress_notes.md §11). Read + edit only; adding a new
// entry requires picking a visit, so that happens from the visit screen, not here.

const PatientVisitNotesTab: React.FC<{ patientId: string }> = ({ patientId }) => {
  const [entries, setEntries] = useState<PatientVisitNoteEntryResponse[]>([]);
  const [loading, setLoading] = useState(true);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const [historyOpenId, setHistoryOpenId] = useState<string | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyByEntry, setHistoryByEntry] = useState<Record<string, VisitNoteEntryVersionResponse[]>>({});

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // Single server-side join across all of the patient's visits (see
    // PR #234 review — this replaces a client-side Promise.all fan-out that
    // fired one request per visit and could saturate the Lambda DB pool).
    NotesAPI.listForPatient(patientId)
      .then((list) => { if (!cancelled) setEntries(list); })
      .catch(() => { if (!cancelled) setEntries([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [patientId]);

  const handleEdit = async (entryId: string, body: string, reason: string | undefined) => {
    setEditSaving(true);
    setEditError(null);
    try {
      const updated = await NotesAPI.update(entryId, { body, edit_reason: reason });
      setEntries((prev) => prev.map((e) => (e.entry_id === entryId ? { ...updated, visit_date: e.visit_date } : e)));
      setEditingId(null);
      setHistoryByEntry((prev) => { const { [entryId]: _drop, ...rest } = prev; return rest; });
    } catch (e) {
      setEditError(e instanceof NotesApiError ? e.message : "Failed to save edit");
    } finally {
      setEditSaving(false);
    }
  };

  const handleToggleHistory = async (entryId: string) => {
    if (historyOpenId === entryId) { setHistoryOpenId(null); return; }
    setHistoryOpenId(entryId);
    if (historyByEntry[entryId]) return;
    setHistoryLoading(true);
    try {
      const versions = await NotesAPI.history(entryId);
      setHistoryByEntry((prev) => ({ ...prev, [entryId]: versions }));
    } catch {
      /* leave empty — the toggle can be retried */
    } finally {
      setHistoryLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm px-4 pt-4 pb-3">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center text-blue-500">
            <IcNote />
          </div>
          <h2 className="text-[15px] font-bold text-slate-900">Visit Notes</h2>
          {entries.length > 0 && (
            <span className="text-xs font-semibold text-slate-500 bg-slate-100 rounded-full px-2 py-0.5 leading-none">
              {entries.length}
            </span>
          )}
        </div>
      </div>
      <p className="text-xs text-slate-400 mb-3">Across all visits. To add a new entry, open the relevant visit.</p>

      {loading && <p className="text-xs text-slate-400 text-center py-4">Loading…</p>}
      {!loading && entries.length === 0 && (
        <p className="text-xs text-slate-400 text-center py-4">No visit notes yet</p>
      )}

      {!loading && entries.map((entry, i) => (
        <div key={entry.entry_id} className={i < entries.length - 1 ? "border-b border-slate-100" : ""}>
          <div className="pt-3">
            <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">
              Visit — {fmtDate(entry.visit_date)}
            </span>
          </div>
          <NoteRow
            entry={entry}
            onEdit={() => { setEditingId(entry.entry_id); setEditError(null); }}
            isEditing={editingId === entry.entry_id}
            editForm={
              <NoteForm
                initialBody={entry.body}
                showSection={false}
                showReason
                onSave={(body, _section, reason) => handleEdit(entry.entry_id, body, reason)}
                onCancel={() => { setEditingId(null); setEditError(null); }}
                saving={editSaving}
                error={editError}
              />
            }
            historyOpen={historyOpenId === entry.entry_id}
            historyVersions={historyByEntry[entry.entry_id] ?? null}
            historyLoading={historyLoading && historyOpenId === entry.entry_id}
            onToggleHistory={() => handleToggleHistory(entry.entry_id)}
          />
        </div>
      ))}
    </div>
  );
};

// ─── BillingTab ───────────────────────────────────────────────────────────────

const BillingTab: React.FC<{
  invoices: InvoiceResponse[];
  highlightInvoiceId?: string | null;
  /** Omitted when the role lacks Create Bills — the button is hidden. */
  onCreateBill?: () => void;
  onInvoiceUpdate?: (updated: InvoiceResponse) => void;
}> = ({ invoices, highlightInvoiceId, onCreateBill, onInvoiceUpdate }) => {
  const [statusFilter, setStatusFilter] = useState("all");
  const [methodFilter, setMethodFilter] = useState("all");
  const [search, setSearch] = useState("");
  const highlightRowRef = useRef<HTMLTableRowElement>(null);
  const highlightCardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!highlightInvoiceId) return;
    // Only one of these is visible at a time (desktop table vs. mobile cards); scroll whichever is actually laid out.
    const target = (highlightRowRef.current?.offsetParent ? highlightRowRef.current : highlightCardRef.current);
    target?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlightInvoiceId, invoices]);
  const [invoiceForPayment, setInvoiceForPayment] = useState<InvoiceResponse | null>(null);
  const [sendingShare, setSendingShare] = useState<Record<string, "whatsapp" | "email" | null>>({});
  const { gateState: creditsGateState, refetch: refetchWallet } = useWalletBalance();
  const [showBuyCredits, setShowBuyCredits] = useState(false);

  const handleShare = async (invoiceId: string, channel: "whatsapp" | "email") => {
    setSendingShare((prev) => ({ ...prev, [invoiceId]: channel }));
    try {
      if (channel === "whatsapp") {
        await sendInvoiceWhatsapp(invoiceId);
        toast.success("Invoice sent via WhatsApp");
      } else {
        await sendInvoiceEmail(invoiceId);
        toast.success("Invoice sent via email");
      }
    } catch (err: any) {
      if (isInsufficientCreditsError(err)) {
        setShowBuyCredits(true);
      } else {
        const msg = err?.message || `Failed to send via ${channel}`;
        toast.error(msg);
      }
    } finally {
      setSendingShare((prev) => ({ ...prev, [invoiceId]: null }));
    }
  };

  const filtered = invoices.filter((inv) => {
    const isPaid = inv.status === "paid";
    const isUnpaid = ["sent", "overdue", "partially_paid", "draft"].includes(inv.status);
    const matchStatus = statusFilter === "all" || (statusFilter === "paid" && isPaid) || (statusFilter === "unpaid" && isUnpaid);
    const matchMethod = methodFilter === "all" || inv.payment_method === methodFilter;
    const q = search.trim().toLowerCase();
    const matchSearch = !q || inv.invoice_number.toLowerCase().includes(q) || (inv.bill_date || inv.created_at || "").includes(search.trim());
    return matchStatus && matchMethod && matchSearch;
  });

  return (
    <>
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm">
        {/* Header */}
        <div className="flex flex-col gap-3 p-4 md:p-6 pb-0">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-lg font-semibold text-slate-900">Invoices</h3>
            {onCreateBill && (
              <button
                onClick={onCreateBill}
                className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-medium text-white bg-gradient-to-r from-[#0092b8] to-[#155dfc] rounded-lg hover:opacity-90 transition shrink-0"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                <span className="hidden sm:inline">Create Bill</span>
              </button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center bg-slate-100 rounded-lg p-0.5">
              {(["all", "paid", "unpaid"] as const).map((val) => (
                <button
                  key={val}
                  onClick={() => setStatusFilter(val)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${statusFilter === val ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                >
                  {val === "all" ? "All" : val === "paid" ? "Paid" : "Unpaid"}
                </button>
              ))}
            </div>
            <select
              value={methodFilter}
              onChange={(e) => setMethodFilter(e.target.value)}
              className="appearance-none bg-white border border-slate-200 text-xs font-medium text-slate-600 rounded-lg pl-3 pr-7 py-2 focus:outline-none cursor-pointer"
              style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 8px center" }}
            >
              <option value="all">All methods</option>
              <option value="cash">Cash</option>
              <option value="upi">UPI</option>
              <option value="cheque">Cheque</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="other">Other</option>
            </select>
            <div className="flex items-center bg-white border border-slate-200 rounded-lg px-3 h-8 flex-1 min-w-[140px] md:flex-none md:w-[200px]">
              <svg className="shrink-0 text-slate-400 mr-2" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by Date/Year" className="flex-1 bg-transparent text-xs text-slate-600 placeholder:text-slate-400 border-0 focus:outline-none" />
            </div>
          </div>
          <div className="flex items-start gap-2.5 px-3 md:px-4 py-2.5 md:py-3 rounded-xl bg-[#f0fbfd] border border-[#cefafe] text-[11px] md:text-xs text-[#007595]">
            <svg className="shrink-0 mt-0.5" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
            Patients pay directly to your clinic via UPI, cash, or bank transfer. Record payment here once received.
          </div>
        </div>

        {invoices.length === 0 ? (
          <div className="text-center py-16 text-sm text-slate-400">No invoices found.</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-sm text-slate-400">No invoices match the current filters.</div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-t border-b border-slate-100 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    <th className="px-6 py-3">Invoice</th>
                    <th className="px-6 py-3">Bill date</th>
                    <th className="px-6 py-3">Amount</th>
                    <th className="px-6 py-3">Status</th>
                    <th className="px-6 py-3">Payment</th>
                    <th className="px-6 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map((inv) => {
                    const st = getInvoiceStatusPresentation(inv);
                    const invDate = new Date(inv.bill_date || inv.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
                    const paymentDisplay = inv.payment_method
                      ? `${inv.payment_method === "bank_transfer" ? "Bank Transfer" : inv.payment_method === "upi" ? "UPI" : inv.payment_method.charAt(0).toUpperCase() + inv.payment_method.slice(1)}${inv.payment_reference ? ` - ${inv.payment_reference}` : ""}`
                      : "-";
                    const isHighlighted = !!highlightInvoiceId && inv.invoice_id === highlightInvoiceId;
                    return (
                      <tr
                        key={inv.invoice_id}
                        ref={isHighlighted ? highlightRowRef : undefined}
                        className={`hover:bg-slate-50 transition-colors ${isHighlighted ? "bg-cyan-50 ring-2 ring-inset ring-cyan-300" : ""}`}
                      >
                        <td className="px-6 py-4 font-medium text-slate-900">{inv.invoice_number}</td>
                        <td className="px-6 py-4 text-slate-600">{invDate}</td>
                        <td className="px-6 py-4 font-semibold text-slate-900">{inv.total_display}</td>
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center gap-1.5" title={st.title}>
                            <span className={`w-2 h-2 rounded-full ${st.dot}`} />
                            <span className={`text-xs font-medium ${st.text}`}>{st.label}</span>
                          </span>
                        </td>
                        <td className="px-6 py-4 text-[#007595] text-xs font-medium">{paymentDisplay}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2 justify-end">
                            {inv.status === "paid" && (
                              <button title="Download invoice PDF" onClick={async () => { try { const { pdf_url } = await getInvoicePdf(inv.invoice_id); window.open(pdf_url, "_blank", "noopener,noreferrer"); } catch { toast.error("PDF not available."); } }} className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition">
                                <PrintIconSm />
                              </button>
                            )}
                            {inv.status !== "draft" && (
                              <>
                                <button
                                  title={creditsGateState === "zero" ? "Out of notification credits — buy more to send" : "Send via WhatsApp"}
                                  disabled={!!sendingShare[inv.invoice_id] || creditsGateState === "zero"}
                                  onClick={() => handleShare(inv.invoice_id, "whatsapp")}
                                  className="p-1.5 rounded-md text-slate-400 hover:text-green-600 hover:bg-green-50 transition disabled:opacity-50"
                                >
                                  {sendingShare[inv.invoice_id] === "whatsapp" ? <SpinnerSm /> : <WhatsAppIconSm />}
                                </button>
                                <button
                                  title={creditsGateState === "zero" ? "Out of notification credits — buy more to send" : "Send via Email"}
                                  disabled={!!sendingShare[inv.invoice_id] || creditsGateState === "zero"}
                                  onClick={() => handleShare(inv.invoice_id, "email")}
                                  className="p-1.5 rounded-md text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition disabled:opacity-50"
                                >
                                  {sendingShare[inv.invoice_id] === "email" ? <SpinnerSm /> : <EmailIconSm />}
                                </button>
                              </>
                            )}
                            {(inv.status === "sent" || inv.status === "overdue" || inv.status === "partially_paid") && (
                              <button onClick={() => setInvoiceForPayment(inv)} className="px-3 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-[#00b8db] to-[#2b7fff] rounded-lg hover:opacity-90 transition">
                                Mark as Paid
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-slate-100">
              {filtered.map((inv) => {
                const st = getInvoiceStatusPresentation(inv);
                const invDate = new Date(inv.bill_date || inv.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
                const paymentDisplay = inv.payment_method ? `${inv.payment_method === "bank_transfer" ? "Bank Transfer" : inv.payment_method === "upi" ? "UPI" : inv.payment_method.charAt(0).toUpperCase() + inv.payment_method.slice(1)}${inv.payment_reference ? ` - ${inv.payment_reference}` : ""}` : null;
                const isHighlighted = !!highlightInvoiceId && inv.invoice_id === highlightInvoiceId;
                return (
                  <div
                    key={inv.invoice_id}
                    ref={isHighlighted ? highlightCardRef : undefined}
                    className={`px-4 py-4 flex items-center justify-between gap-3 ${isHighlighted ? "bg-cyan-50 ring-2 ring-inset ring-cyan-300" : ""}`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-slate-900">{inv.invoice_number}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{invDate}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <p className="text-sm font-bold text-slate-900">{inv.total_display}</p>
                      <span className="inline-flex items-center gap-1.5" title={st.title}>
                        <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
                        <span className={`text-[11px] font-medium ${st.text}`}>{st.label}</span>
                      </span>
                      {paymentDisplay && <span className="text-[11px] text-[#007595] font-medium">{paymentDisplay}</span>}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {inv.status !== "draft" && (
                        <>
                          <button
                            title={creditsGateState === "zero" ? "Out of notification credits — buy more to send" : "Send via WhatsApp"}
                            disabled={!!sendingShare[inv.invoice_id] || creditsGateState === "zero"}
                            onClick={() => handleShare(inv.invoice_id, "whatsapp")}
                            className="p-1.5 rounded-md text-slate-400 hover:text-green-600 hover:bg-green-50 transition disabled:opacity-50"
                          >
                            {sendingShare[inv.invoice_id] === "whatsapp" ? <SpinnerSm /> : <WhatsAppIconSm />}
                          </button>
                          <button
                            title={creditsGateState === "zero" ? "Out of notification credits — buy more to send" : "Send via Email"}
                            disabled={!!sendingShare[inv.invoice_id] || creditsGateState === "zero"}
                            onClick={() => handleShare(inv.invoice_id, "email")}
                            className="p-1.5 rounded-md text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition disabled:opacity-50"
                          >
                            {sendingShare[inv.invoice_id] === "email" ? <SpinnerSm /> : <EmailIconSm />}
                          </button>
                        </>
                      )}
                      {(inv.status === "sent" || inv.status === "overdue" || inv.status === "partially_paid") && (
                        <button onClick={() => setInvoiceForPayment(inv)} className="px-2.5 py-1.5 text-[11px] font-medium text-white bg-gradient-to-r from-[#00b8db] to-[#2b7fff] rounded-lg hover:opacity-90 transition">
                          Mark as Paid
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {invoiceForPayment && (
        <PaymentModal
          invoice={invoiceForPayment}
          onClose={() => setInvoiceForPayment(null)}
          onSuccess={(updated) => {
            onInvoiceUpdate?.(updated);
            setInvoiceForPayment(null);
          }}
        />
      )}

      {showBuyCredits && (
        <BuyCreditsModal onClose={() => setShowBuyCredits(false)} onPurchased={refetchWallet} />
      )}
    </>
  );
};

// ─── DentalDocumentsTab ───────────────────────────────────────────────────────

const DentalDocumentsTab: React.FC<{ visits: VisitResponse[] }> = ({ visits }) => {
  const [viewFindings, setViewFindings] = useState<{ findings: ToothFinding[]; date: string } | null>(null);

  // Filter finalized visits that have dental findings in chief complaint
  const dentalVisits = useMemo(() => {
    return visits
      .filter((v) => {
        const isFinal = ["Finalized", "finalized", "completed"].includes(v.status);
        const hasDental = (v.dental_chart?.findings.length ?? 0) > 0 || v.chief_complaint?.includes("Dental Findings:");
        return isFinal && hasDental;
      })
      .sort((a, b) => new Date(b.visit_date).getTime() - new Date(a.visit_date).getTime());
  }, [visits]);

  return (
    <>
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-900">Documents</h3>
          <span className="text-xs text-slate-400">
            {dentalVisits.length} dental record{dentalVisits.length !== 1 ? "s" : ""}
          </span>
        </div>

        {dentalVisits.length === 0 ? (
          <div className="text-center py-16 flex flex-col items-center gap-3">
            <div className="w-14 h-14 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-center">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="1.5">
                <path d="M12 2C9 2 6 4 6 7c0 2 1 3.5 1.5 5.5C8 15 8.5 22 12 22s4-7 4.5-9.5C17 10.5 18 9 18 7c0-3-3-5-6-5z" />
              </svg>
            </div>
            <p className="text-sm text-slate-400">No dental finding records yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-5">
            {dentalVisits.map((v) => {
              // The stored chart is complete; the text block only knows the
              // original ten conditions, so it is the fallback for older visits.
              const findings: ToothFinding[] = v.dental_chart?.findings.length
                ? v.dental_chart.findings
                : parseDentalFindingsText(v.chief_complaint ?? "");
              const teethCount = findings.length;
              const condCount = findings.reduce((s, f) => s + f.conditions.length, 0);
              const hasMobility = findings.some((f) => f.mobility_grade);

              return (
                <button
                  key={v.visit_id}
                  onClick={() => setViewFindings({ findings, date: v.visit_date })}
                  className="bg-white border border-slate-200 rounded-xl p-4 hover:border-blue-300 hover:shadow-md transition-all text-left group"
                >
                  {/* Mini tooth preview row */}
                  <div className="flex items-center gap-1 mb-3 overflow-hidden">
                    {findings.slice(0, 8).map((f) => (
                      <div key={f.tooth} className="w-6 h-8 flex-shrink-0">
                        <svg viewBox="0 0 28 48" overflow="visible" className="w-full h-full">
                          <path
                            d="M12 2 L11 20 L5 24 L5 40 Q5 48 14 48 Q23 48 23 40 L23 24 L17 20 L16 2 Z"
                            stroke="#3b82f6"
                            strokeWidth={1.5}
                            fill="#eff6ff"
                            strokeLinejoin="round"
                          />
                          {f.conditions.slice(0, 3).map((c, i) => {
                            const color = CONDITION_BY_KEY[c as keyof typeof CONDITION_BY_KEY]?.color ?? "#94a3b8";
                            return <circle key={i} cx={i * 10 + 4} cy={38} r={2.5} fill={color} />;
                          })}
                        </svg>
                      </div>
                    ))}
                    {findings.length > 8 && (
                      <span className="text-xs text-slate-400 ml-1">+{findings.length - 8}</span>
                    )}
                  </div>

                  {/* Date */}
                  <p className="text-sm font-semibold text-slate-800">{fmtDate(v.visit_date)}</p>

                  {/* Summary */}
                  <p className="text-xs text-slate-500 mt-1">
                    {teethCount} {teethCount === 1 ? "tooth" : "teeth"} &middot; {condCount} {condCount === 1 ? "condition" : "conditions"}
                    {hasMobility && " \u00B7 mobility noted"}
                  </p>

                  {/* Status badge */}
                  <div className="flex items-center gap-1.5 mt-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-500" />
                    <span className="text-xs font-medium text-emerald-700">Finalized</span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Read-only dental chart modal */}
      {viewFindings && (
        <ToothChart
          isOpen
          onClose={() => setViewFindings(null)}
          findings={viewFindings.findings}
          onChange={() => {}}
          readOnly
        />
      )}
    </>
  );
};

// ─── PlaceholderTab ────────────────────────────────────────────────────────────

const PlaceholderTab: React.FC<{ label: string; icon: React.ReactNode }> = ({ label, icon }) => (
  <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-12 flex flex-col items-center gap-3">
    <div className="w-14 h-14 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-center">
      {icon}
    </div>
    <p className="text-base font-semibold text-slate-600">{label}</p>
    <p className="text-sm text-slate-400 text-center max-w-xs">
      This section is under development and will be available in a future update.
    </p>
  </div>
);

// ─── Tiny inline icons ────────────────────────────────────────────────────────

const PhoneIconSm = () => (
  <svg className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 8.81 19.79 19.79 0 01.12 2.2 2 2 0 012.11 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 7.09a16 16 0 006 6l.46-.46a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
  </svg>
);

const WhatsAppIconSm = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const EmailIconSm = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

const SpinnerSm = () => (
  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
  </svg>
);

const MailIconSm = () => (
  <svg className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" />
  </svg>
);

const PinIconSm = () => (
  <svg className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" />
  </svg>
);

const EditIconSm = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const CalIconSm = () => (
  <svg className="w-4 h-4 text-[#0066ff]" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const PencilIconSm = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);


const PrintIconSm = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2" /><rect x="6" y="14" width="12" height="8" />
  </svg>
);

const CalIconTile = () => (
  <svg className="w-3.5 h-3.5 text-blue-400" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const ActivityIconTile = () => (
  <svg className="w-3.5 h-3.5 text-teal-400" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
  </svg>
);

const RescheduleIconTile = () => (
  <svg className="w-3.5 h-3.5 text-amber-400" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
  </svg>
);

const IdIconTile = () => (
  <svg className="w-3.5 h-3.5 text-purple-400" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
  </svg>
);

const PhoneIconTile = () => (
  <svg className="w-3.5 h-3.5 text-orange-400" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 8.81 19.79 19.79 0 01.12 2.2 2 2 0 012.11 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 7.09a16 16 0 006 6l.46-.46a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
  </svg>
);

export default PatientProfilePage;
